// src/components/InventoryTable.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Search, Loader2, ExternalLink, ArrowUpDown } from "lucide-react";
import { cn } from "../../components/utils/index"; // reuse your utils for money formatting
import { getInventory } from "../../api/inventory";
import Navbar from "../../components/Navbar";
import Subnav from "../../components/inventory/SubNav";
import { Pencil } from "lucide-react";
import { updateInventory } from "../../api/inventory"; // 
import { useAlert } from "../../context/AlertContext";

// -----------------------------
// Table Header with sorting
// -----------------------------
function ThSortable({ children, active, dir, onToggle }) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
    >
      <button
        onClick={onToggle}
        className="inline-flex items-center gap-1 hover:underline"
      >
        {children}
        <ArrowUpDown
          className={cn(
            "h-4 w-4 transition",
            active ? "opacity-100" : "opacity-40"
          )}
        />
      </button>
    </th>
  );
}

// -----------------------------
// Pagination
// -----------------------------
function Paginator({ page, pageSize, total, onPageChange, onPageSizeChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3">
      <div className="text-sm text-gray-500">
        Page {page} of {pages} • {total.toLocaleString()} products
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Rows</label>
        <select
          className="rounded-xl border px-3 py-1.5 bg-white shadow-sm"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
          >
            ◀
          </button>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
          >
            ▶
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------
// Main Inventory Table
// -----------------------------
export default function InventoryTable({ fetchInventory }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState({ field: "name", dir: "asc" });
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newBottles, setNewBottles] = useState("");
  const { showAlert } = useAlert();
  const handleConfirmUpdate = async () => {
    // Validate newBottles (ensure it's a valid number)
    if (!newBottles || isNaN(newBottles) || newBottles < 0) {
      showAlert({ message: "Please enter a valid number of bottles." })
      return;
    }

    try {
      // Call the updateInventory function to update stock
      const res = await updateInventory(selectedItem.id, {
        on_hand_bottles: Number(newBottles), // Send updated stock count
      });

      if (!res?.result?.affectedRows) {
        showAlert({ message: "Failed to update! Please contact admin." })
        return;
      }

      // Update the local state without reloading the page
      const updatedProducts = products.map((item) =>
        item.id === selectedItem.id ? { ...item, on_hand_bottles: newBottles } : item
      );
      showAlert({ type: "success", message: "Inventory updated successfully!" })
      setProducts(updatedProducts); // Update local state with new stock
      setShowModal(false); // Close the modal after update

    } catch (err) {
      showAlert({ message: "Failed to update! Please contact admin." })
    }
  };


  const qDeb = useMemo(() => q, [q]);

  async function fetchInventory({ page, pageSize, q, sort }) {
    const params = { page, pageSize };
    if (q) params.q = q;
    if (sort?.field) {
      params.sortField = sort.field;
      params.sortDir = sort.dir;
    }

    const res = await getInventory(params);
    return res;
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetchInventory({ page, pageSize, q: qDeb, sort });
        if (alive) {
          setProducts(r.data || []);
          setTotal(r.total || 0);
        }
      } catch (err) {
        console.error("Failed to fetch inventory:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => (alive = false);
  }, [page, pageSize, qDeb, sort]);

  const toggleSort = (field) => {
    setSort((prev) =>
      prev.field !== field
        ? { field, dir: "asc" }
        : { field, dir: prev.dir === "asc" ? "desc" : "asc" }
    );
  };

  return (
    <div>
      <Navbar />
      <Subnav />
      <div className="max-w-7xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Inventory</h1>

        <div className="flex justify-end mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search by product, brand, category..."
              className="w-72 rounded-2xl border bg-white pl-9 pr-3 py-2 shadow-sm focus:outline-none focus:ring-2"
            />
          </div>
        </div>

        {/* Table */}
        <table className="min-w-full divide-y divide-gray-200 rounded-xl overflow-hidden shadow-lg">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <ThSortable
                active={sort.field === "brand"}
                dir={sort.dir}
                onToggle={() => toggleSort("brand")}
              >
                Brand
              </ThSortable>
              <ThSortable
                active={sort.field === "name"}
                dir={sort.dir}
                onToggle={() => toggleSort("name")}
              >
                Product
              </ThSortable>
              <ThSortable
                active={sort.field === "size_label"}
                dir={sort.dir}
                onToggle={() => toggleSort("size_label")}
              >
                Size
              </ThSortable>
              <ThSortable
                active={sort.field === "on_hand_bottles"}
                dir={sort.dir}
                onToggle={() => toggleSort("on_hand_bottles")}
              >
                Bottles
              </ThSortable>
              <ThSortable
                active={sort.field === "on_hand_cases"}
                dir={sort.dir}
                onToggle={() => toggleSort("on_hand_cases")}
              >
                Cases
              </ThSortable>
              <ThSortable
                active={sort.field === "case_pack_qty"}
                dir={sort.dir}
                onToggle={() => toggleSort("case_pack_qty")}
              >
                Bottles per Case
              </ThSortable>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Total Stock
              </th>
              {/* <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Actions
              </th> */}
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-500">
                  <Loader2 className="inline h-5 w-5 animate-spin mr-2" />
                  Loading inventory…
                </td>
              </tr>
            )}

            {!loading && products.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-500">
                  No products found.
                </td>
              </tr>
            )}

            {!loading &&
              products.map((p) => {
                // Determine low-stock color
                const totalBottles =
                  p.on_hand_bottles +
                  (p.on_hand_cases || 0) * (p.case_pack_qty || 0);
                const stockColor =
                  totalBottles === 0
                    ? "text-rose-600 font-semibold"
                    : totalBottles < 10
                      ? "text-amber-600 font-semibold"
                      : "text-gray-900";
                const caseQty = Math.floor(
                  totalBottles / (p.case_pack_qty || 1)
                );
                const remainingBottles = totalBottles % (p.case_pack_qty || 1);

                return (
                  <tr
                    key={p.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {p.brand}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {p.name}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {p.size_label}
                    </td>
                    <td className="px-4 py-3 text-sm">{p.on_hand_bottles}</td>
                    <td className={`px-4 py-3 text-sm ${stockColor}`}>
                      {caseQty > 0 &&
                        `${caseQty} case${caseQty > 1 ? "s" : ""}`}
                      {remainingBottles > 0 &&
                        `${caseQty > 0 ? " + " : ""}${remainingBottles} bottle${remainingBottles > 1 ? "s" : ""
                        }`}
                      {caseQty === 0 &&
                        remainingBottles === 0 &&
                        "Out of stock"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {p.case_pack_qty || 0}
                    </td>
                    <td className={`px-4 py-3 text-sm ${stockColor}`}>
                      <div className="flex items-center justify-between">
                        {totalBottles}

                        <button
                          onClick={() => {
                            setSelectedItem(p);
                            setNewBottles(p.on_hand_bottles);
                            setShowModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 ml-2"
                        >
                          <Pencil size={16} />
                        </button>
                      </div>
                    </td>

                    {/* <td className="px-4 py-3 text-sm">
                      <a
                        href={`/products/${p.id}`}
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" /> View
                      </a>
                    </td> */}
                  </tr>
                );
              })}
          </tbody>
        </table>

        {/* Pagination */}
        <Paginator
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
        />
        {/* Edit Stock Modal */}
        {showModal && selectedItem && (
          <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-xl w-[450px]">

              <h2 className="text-xl font-bold mb-4 text-center">Update Stock</h2>

              <div className="mb-3">
                <p className="font-semibold">{selectedItem.name}</p>
                <p className="text-sm text-gray-600">{selectedItem.size_label}</p>
              </div>

              <p className="text-gray-700 mb-1">
                Current Stock: <b>{selectedItem.on_hand_bottles}</b> Bottles
              </p>

              <label className="text-red-500 font-semibold">
                Update Stock (Only Bottles)
              </label>

              <input
                type="number"
                value={newBottles}
                onChange={(e) => setNewBottles(e.target.value)}
                className="border border-black rounded w-full p-2 mt-1 mb-4"
              />

              <div className="flex justify-between mt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="text-red-500 font-semibold"
                >
                  Cancel
                </button>

                <button
                  onClick={handleConfirmUpdate}
                  className="bg-green-500 text-white px-6 py-2 rounded-lg"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
